import type { sessionReplayPlugin } from "@amplitude/plugin-session-replay-browser";

/**
 * Session Replay privacy markers.
 *
 * Kept in a standalone module so UI code can tag elements without importing the
 * Amplitude bundle. Mark an element we own with `data-amp-unmask` to let its
 * text appear in replays. Unmasking cascades to descendants, so only mark
 * content that is safe to capture and never target library internals such as
 * antd's `ant-*` classes: those are private API and may change meaning.
 *
 * Use the class marker only where a data attribute can't be attached, such as
 * an antd `Select` that exposes `className`/`classNames` but not arbitrary
 * attributes.
 *
 * `data-amp-mask` re-masks a control inside an unmasked region. This lets a
 * filter panel show its labels and option text while keeping the values typed
 * into (or selected in) its controls hidden. Masking wins over unmasking.
 */
export const AMP_UNMASK_ATTRIBUTE = "data-amp-unmask";

export const AMP_UNMASK_SELECTOR = `[${AMP_UNMASK_ATTRIBUTE}]`;

export const AMP_UNMASK_CLASS = "amp-unmask";

export const AMP_UNMASK_CLASS_SELECTOR = `.${AMP_UNMASK_CLASS}`;

export const AMP_MASK_ATTRIBUTE = "data-amp-mask";

export const AMP_MASK_CLASS = "amp-mask";

export const AMP_MASK_PROPS = { [AMP_MASK_ATTRIBUTE]: "" } as const;

export const AMP_BLOCK_CLASS = "amp-block";

export const AMP_BLOCK_SELECTOR = `.${AMP_BLOCK_CLASS}`;

export const AMP_UNMASK_PROPS = { [AMP_UNMASK_ATTRIBUTE]: "" } as const;

type SessionReplayPrivacyConfig = NonNullable<
  NonNullable<Parameters<typeof sessionReplayPlugin>[0]>["privacyConfig"]
>;

export const SESSION_REPLAY_PRIVACY_CONFIG = {
  defaultMaskLevel: "conservative",
  blockSelector: [AMP_BLOCK_SELECTOR],
  unmaskSelector: [AMP_UNMASK_SELECTOR, AMP_UNMASK_CLASS_SELECTOR],
  // Text masking does not touch attributes
  maskAttributes: ["title", "value"],
} satisfies SessionReplayPrivacyConfig;
